'use client';

import Image from 'next/image';
import Link from 'next/link';

import AppleAppStoreLogo from '@/assets/icons/apple-app-store-icon.svg';
import FacebookLogo from '@/assets/icons/facebook-icon.svg';
import GooglePlayStoreLogo from '@/assets/icons/google-play-store-logo.svg';
import InstagramLogo from '@/assets/icons/instagram-icon.svg';
import RssFeedLogo from '@/assets/icons/rss-feed-icon.svg';
import StocktwitsSquareLogo from '@/assets/icons/stocktwits-logo-square.svg';
import ThematicSquareLogo from '@/assets/icons/thematic-logo-square.svg';
import XLogo from '@/assets/icons/x-twitter.svg';
import YoutubeLogo from '@/assets/icons/youtube-icon.svg';
import StocktwitsLogo from '@/assets/logos/stocktwits-logo.svg';
import { Heading, Paragraph } from '@/components/dom/text-elements';
import Button from '@/components/global/button';
import { useGlobalState } from '@/components/global/global-state';
import SignupLoginButtonModal from '@/components/global/signup-login-button-modal';
import UserProfileDropdown from '@/components/global/user-profile-dropdown';
import CircleBurst from '@/components/ui/CircleBurst';

export const AcquisitionAnnouncementHomepage = () => {
    const { globalState } = useGlobalState();
    const { currentUser } = globalState;

    return (
        <main className="flex-grow max-h-screen p-4">
            <div className="max-w-screen-2xl mx-auto">
                <div className="relative z-10 mb-12 md:mb-20">
                    <div className="bg-stocktwits-black overflow-hidden flex md:block rounded-lg md:pb-24 lg:pb-32 relative z-20 shadow-md">
                        <CircleBurst
                            className="absolute z-10 md:w-[250%] md:h-[250%] top-1/4 left-0 -translate-y-1/2 -translate-x-1/2 to-70% to-stocktwits-black"
                            colorType="stocktwits-blue"
                        />

                        <section className="flex-grow relative z-20 bg-gradient-to-b from-white from-50% to-thematic-light-blue border h-full md:max-w-3xl md:h-auto md:rounded-br-lg md:rounded-bl-lg lg:border-analyst-gray lg:border-t-0 lg:max-w-4xl xl:max-w-screen-lg mx-auto px-8 pt-6 md:pb-6 lg:pb-10 shadow-sm">
                            <div className="relative xl:absolute xl:top-6 xl:-right-8 xl:translate-x-full z-30 flex justify-end xl:mb-0 mb-8">
                                {!currentUser ? (
                                    <SignupLoginButtonModal
                                        className="relative"
                                        buttonClassName="border-analyst-gray border-2 bg-white/10 xl:text-white px-8 hover:bg-stocktwits-blue font-brand-md hover:text-white"
                                    />
                                ) : (
                                    <UserProfileDropdown className="animate-fadeIn inline-flex" />
                                )}
                            </div>

                            <div className="absolute z-10 bottom-0 left-0 w-full h-full overflow-hidden">
                                <CircleBurst
                                    className="opacity-30 w-full h-full sm:w-screen sm:h-screen translate-y-1/2 -translate-x-1/2 to-20% sm:to-60%"
                                    colorType="stocktwits-blue"
                                />
                            </div>

                            <div className="flex items-center justify-center gap-2 w-full mb-12 relative z-20">
                                <ThematicSquareLogo className="w-12 h-12" />

                                <div className="flex items-center gap-2 w-full">
                                    <span className="w-2 h-2 bg-analyst-gray rounded-full" />
                                    <hr className="border-dashed border-analyst-gray flex-grow" />
                                    <span className="w-2 h-2 bg-analyst-gray rounded-full" />
                                </div>

                                <StocktwitsSquareLogo className="w-12 h-12" />
                            </div>

                            <div className="grid grid-cols-12 relative z-20">
                                <div className="col-span-12 md:col-span-7 lg:col-span-6 flex flex-col gap-6 items-start md:mb-0 mb-20">
                                    <span className="bg-analyst-lavender-medium/25 uppercase text-xs py-2 px-4 rounded-lg font-brand-bold text-brand-blue-yves tracking-wider">
                                        Update
                                    </span>

                                    <Heading
                                        importance={1}
                                        className="font-alt-riveria-bold sm:text-5xl lg:text-6xl !leading-[1.25]"
                                    >
                                        Thematic joins Stocktwits
                                    </Heading>

                                    <div className="bg-white p-4 rounded-lg border border-analyst-dark-lavender sm:mb-4">
                                        <Paragraph className="mb-0">
                                            Stocktwits is the leading social investing platform, with over 10 million
                                            users across 200&#43; countries. Powered by 17 years of proprietary data and
                                            real&#45;time sentiment&mdash;and now accelerated by its acquisition of
                                            Thematic&mdash;Stocktwits is delivering alpha&#45;driving insights and a new
                                            generation of AI&#45;powered research, discovery, and decision&#45;making
                                            tools.
                                        </Paragraph>
                                    </div>

                                    <div className="flex flex-col gap-4 items-center md:flex-row w-full">
                                        <Button
                                            type="link"
                                            color="custom"
                                            href="https://stocktwits.com/"
                                            openNewWindow={true}
                                            roundedCorners="full"
                                            className="bg-gradient-to-r from-stocktwits-blue to-[#143BA5] text-white font-brand-md px-8 hover:to-stocktwits-blue transition-all"
                                        >
                                            Visit Stocktwits
                                        </Button>

                                        <Link
                                            href="https://stocktwits.com/news-articles/business/others/stocktwits-acquires-ai-startup-thematic/ch8KVxsR5pd"
                                            rel="external"
                                            target="_blank"
                                            className="font-brand-md text-sm text-black hover:text-stocktwits-blue transition-colors"
                                        >
                                            Read the full announcement &rarr;
                                        </Link>
                                    </div>
                                </div>

                                <div className="relative flex-grow flex items-end col-span-12 md:col-span-5 lg:col-span-6 z-20">
                                    <Image
                                        src="/assets/homepage/stocktwits-thematic-phone.png"
                                        className="origin-center drop-shadow-no-bottom translate-x-[5%]  md:scale-140 xl:scale-130 md:-translate-y-[15%] md:translate-x-[15%] xl:-translate-y-[10%] w-full"
                                        priority={true}
                                        alt="Stocktwits mobile app and YouTube video"
                                        width={1400}
                                        height={1600}
                                    />
                                </div>
                            </div>
                        </section>
                    </div>
                    <CircleBurst
                        className="absolute z-10 w-[500px] h-[500px] bottom-0 left-1/2 translate-y-1/2 -translate-x-1/2 to-60% opacity-70"
                        colorType="thematic-light-yellow"
                    />
                </div>

                <footer className="flex flex-col gap-4 md:gap-0 md:flex-row md:items-center md:justify-between relative z-20">
                    <div className="flex flex-col gap-4 items-start md:block">
                        <StocktwitsLogo className="h-6 text-black inline-block align-baseline mr-2" />
                        <small className="text-xs text-black">&copy;2025 StockTwits, Inc. All rights reserved.</small>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-8">
                        <div className="flex items-center gap-6">
                            <Link
                                href="https://twitter.com/stocktwits"
                                title="Stocktwits on X"
                                className="text-black hover:text-stocktwits-blue transition-colors"
                                rel="noopener nofollow"
                                target="_blank"
                            >
                                <XLogo className="h-5" />
                            </Link>
                            <Link
                                href="https://www.instagram.com/stocktwits/"
                                title="Stocktwits on Instagram"
                                className="text-black hover:text-stocktwits-blue transition-colors"
                                rel="noopener nofollow"
                                target="_blank"
                            >
                                <InstagramLogo className="h-5" />
                            </Link>
                            <Link
                                href="https://facebook.com/stocktwits"
                                title="Stocktwits on Facebook"
                                className="text-black hover:text-stocktwits-blue transition-colors"
                                rel="noopener nofollow"
                                target="_blank"
                            >
                                <FacebookLogo className="h-5" />
                            </Link>
                            <Link
                                href="https://www.youtube.com/@stocktwits"
                                title="Stocktwits on YouTube"
                                className="text-black hover:text-stocktwits-blue transition-colors"
                                rel="noopener nofollow"
                                target="_blank"
                            >
                                <YoutubeLogo className="h-5" />
                            </Link>
                            <Link
                                href="https://www.stocktwits.com/sitemap/rss_feed.xml"
                                title="Stocktwits RSS Feed"
                                className="text-black hover:text-stocktwits-blue transition-colors"
                                rel="noopener nofollow"
                                target="_blank"
                            >
                                <RssFeedLogo className="h-5" />
                            </Link>
                        </div>

                        <div className="flex items-center gap-4">
                            <Link
                                href="https://stocktwits.onelink.me/NjIT/ttq0iuad"
                                title="Download Stocktwits on Apple App Store"
                                className="text-black hover:text-stocktwits-blue transition-colors"
                                rel="noopener nofollow"
                                target="_blank"
                            >
                                <AppleAppStoreLogo className="h-10" />
                            </Link>
                            <Link
                                href="https://stocktwits.onelink.me/NjIT/l3d56r44"
                                title="Download Stocktwits on Google Play"
                                className="text-black hover:text-stocktwits-blue transition-colors"
                                rel="noopener nofollow"
                                target="_blank"
                            >
                                <GooglePlayStoreLogo className="h-10" />
                            </Link>
                        </div>
                    </div>
                </footer>
            </div>
        </main>
    );
};
